import { initReactI18next } from 'react-i18next';

import i18n, { BaseAppLanguageManager, IBaseAppLanguageManagerParams } from '@aqx/i18n';

import cookies, { ECookieKey } from '@aqx-core/common/cookies';

import { broadcastLanguageChangeMessage } from '../hooks/syncing/language';
import storage, { EStorageKey } from '../storage';
import { checkIsNative } from '../validators';
import { changeDayLocale } from './date';
import { APP_LANGUAGES } from './languages';
import { getResourceBundle } from './resource';
import { TAppLanguageISOCountryCode } from './types';

export * from './types';

class AppLanguageManager extends BaseAppLanguageManager<TAppLanguageISOCountryCode> {
  private languageChangeRestartCallback: () => void = () => {};

  constructor(params: IBaseAppLanguageManagerParams<TAppLanguageISOCountryCode>) {
    super(params);
  }

  public setLanguageChangeRestartCallback(callback: () => void) {
    this.languageChangeRestartCallback = callback;
  }

  public setLanguagePreference(language: TAppLanguageISOCountryCode): void {
    cookies.set(ECookieKey.LANGUAGE, language, { path: '/' });
  }

  public initLanguage = (language: TAppLanguageISOCountryCode) => {
    if (!i18n.hasResourceBundle(language, 'resource')) {
      i18n.addResourceBundle(language, 'resource', getResourceBundle(language));
    }
    i18n.changeLanguage(language);
    updateRootHtmlDirection(isRTL() ? 'rtl' : 'ltr');
  };

  // Only set to cookie when user manually changes language
  public changeLanguage = async (params: {
    language: TAppLanguageISOCountryCode;
    shouldBroadcast?: boolean;
    updateLanguageCallback?: () => Promise<void>; // Callback to update language via API call
  }) => {
    const { language, updateLanguageCallback, shouldBroadcast = true } = params;
    if (!i18n.hasResourceBundle(language, 'resource')) {
      i18n.addResourceBundle(language, 'resource', getResourceBundle(language));
    }
    const currentLanguage = getUserLanguage();
    // Reload page when language is changed to Arabic (Mobile app implementation)
    await i18n.changeLanguage(language);
    updateRootHtmlDirection(isRTL() ? 'rtl' : 'ltr');
    const shouldReload =
      (currentLanguage === 'ar_AE' && language !== 'ar_AE') || (currentLanguage !== 'ar_AE' && language === 'ar_AE');
    changeDayLocale(language);
    this.setLanguagePreference(language);

    if (shouldBroadcast) {
      broadcastLanguageChangeMessage({ lang: language });
    }

    await updateLanguageCallback?.();
    if (shouldReload) {
      // Restart callback is initialized during mobile and web application startup
      this.languageChangeRestartCallback?.();
    }
  };
}

export const fallbackLanguage = APP_LANGUAGES[0].value;

export const getUserLanguage = () => {
  return i18n.language;
};

export const isRTL = () => {
  return getUserLanguage() === 'ar_AE';
};

export const getLanguageFromStorage = () => {
  return checkIsNative() ? storage.getItem(EStorageKey.LANGUAGE) : cookies.get(ECookieKey.LANGUAGE);
};

export const updateRootHtmlDirection = (dir: 'rtl' | 'ltr') => {
  if (checkIsNative()) {
    return;
  }
  document.documentElement.setAttribute('dir', dir);
};

export const appLanguageManager = new AppLanguageManager({
  languages: APP_LANGUAGES,
  fallbackLanguage: APP_LANGUAGES[0],
  resources: {
    en_GB: {},
    zh_CN: {},
    zh_TW: {},
    th_TH: {},
    vi_VN: {},
    ms_MY: {},
    id_ID: {},
    ja_JP: {},
    ko_KR: {},
    ar_AE: {},
    es_ES: {},
    pt_BR: {}
  },
  initReactI18nextInstance: initReactI18next
});

export { i18n };
